import I18n from "components/materials/I18n";
import { ReactNode, useEffect, useState } from "react";
import { toast } from "react-toastify";
import Button, { EButtonVariant } from "components/elements/Button";

const fallback = "Le document n'a pas pu être généré. Réessayez ou contactez le support avec la référence du dossier.";

export async function fetchDocument(url: string, signal?: AbortSignal): Promise<Blob> {
	const response = await fetch(url, { credentials: "include", signal });
	if (!response.ok) {
		const body = await response.json().catch(() => null);
		const message = response.status === 422 && typeof body?.error?.message === "string" ? body.error.message : fallback;
		const labels: Record<string, string> = I18n.asset.enums.EProductDocumentType;
		const label = body?.error?.documentType === "rto" ? "Convention RTO" : labels[body?.error?.documentType];
		throw new Error(label ? `${label} : ${message}` : message);
	}
	// Some streaming endpoints omit Content-Type. Inspect the bytes so a valid
	// download still works, while an HTML login page or JSON error never does.
	const bytes = new Uint8Array(await response.arrayBuffer());
	const isPdf = bytes[0] === 0x25 && bytes[1] === 0x50 && bytes[2] === 0x44 && bytes[3] === 0x46 && bytes[4] === 0x2d;
	const isZip = bytes[0] === 0x50 && bytes[1] === 0x4b && ((bytes[2] === 3 && bytes[3] === 4) || (bytes[2] === 5 && bytes[3] === 6));
	if (!isPdf && !isZip) throw new Error(fallback);
	return new Blob([bytes], { type: isPdf ? "application/pdf" : "application/zip" });
}

function useDownload(url: string, filename?: string) {
	const [busy, setBusy] = useState(false);
	const download = async () => {
		if (busy) return;
		setBusy(true);
		// Open previews synchronously so browsers do not block the tab after the fetch.
		const preview = filename ? null : window.open("about:blank", "_blank");
		if (preview) preview.opener = null;
		try {
			const blob = await fetchDocument(url);
			const objectUrl = URL.createObjectURL(blob);
			if (preview) preview.location.href = objectUrl;
			else {
				const link = document.createElement("a");
				link.href = objectUrl;
				link.download = filename ?? "document.pdf";
				document.body.appendChild(link);
				link.click();
				link.remove();
			}
			window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
		} catch (error) {
			preview?.close();
			toast.error(error instanceof Error ? error.message : fallback, { autoClose: false });
		} finally {
			setBusy(false);
		}
	};
	return { busy, download };
}

export function DocumentDownloadLink({ href, download: filename, children }: { href: string; download?: string; children: ReactNode }) {
	const { busy, download } = useDownload(href, filename);
	return (
		<a
			href={href}
			aria-label={filename ? "Télécharger le document" : "Afficher le document"}
			aria-disabled={busy}
			aria-busy={busy}
			onClick={(event) => {
				event.preventDefault();
				void download();
			}}>
			{children}
		</a>
	);
}

export function DocumentDownloadButton({ url, children, icon }: { url: string; children: ReactNode; icon?: JSX.Element }) {
	const { busy, download } = useDownload(url, "documents.zip");
	return (
		<Button variant={EButtonVariant.OUTLINED} onClick={download} disabled={busy} loading={busy} icon={icon}>
			{children}
		</Button>
	);
}

export function DocumentPreview({ url }: { url: string }) {
	const [objectUrl, setObjectUrl] = useState<string>();
	const [error, setError] = useState<string>();
	const [retry, setRetry] = useState(0);
	useEffect(() => {
		const controller = new AbortController();
		let current: string | undefined;
		setObjectUrl(undefined);
		setError(undefined);
		void fetchDocument(url, controller.signal)
			.then((blob) => {
				if (controller.signal.aborted) return;
				current = URL.createObjectURL(blob);
				setObjectUrl(current);
			})
			.catch((reason: unknown) => {
				if (!controller.signal.aborted) setError(reason instanceof Error ? reason.message : fallback);
			});
		return () => {
			controller.abort();
			if (current) URL.revokeObjectURL(current);
		};
	}, [url, retry]);
	if (error)
		return (
			<div role="alert">
				<p>{error}</p>
				<Button onClick={() => setRetry(retry + 1)}>Réessayer</Button>
			</div>
		);
	if (!objectUrl) return <p role="status">Génération du document en cours…</p>;
	return <object aria-label="RTO Document" data={objectUrl} width="100%" height="900px" type="application/pdf" />;
}
